import { 
  CompletionMessage, 
  FileUpload,
  CompletionRequest,
  MessageContext,
  CompletionFileAttachment,
  StreamingChunk
} from '@app-libs/model';
import { mapMessageRole, validateMessage } from '@app-libs/core';

/**
 * Utility class for transforming messages between frontend DTOs and shared completion types.
 * Handles conversions for the new unified API while maintaining backward compatibility.
 */
export class MessageTransformer {
  
  /**
   * Converts an array of CompletionMessages to MessageContext array for the unified API
   */
  static toMessageContextArray(messages: CompletionMessage[]): MessageContext[] {
    return messages.map(msg => this.toMessageContext(msg));
  }

  /**
   * Converts a single CompletionMessage to MessageContext
   */
  static toMessageContext(message: CompletionMessage): MessageContext {
    const messageContext: MessageContext = {
      role: mapMessageRole(message.role),
      content: message.content || '',
      id: message.message_id,
      timestamp: message.created_at ? new Date(message.created_at) : undefined
    };

    // Handle file attachments
    const attachments = this.extractFileAttachments(message);
    if (attachments.length > 0) {
      messageContext.attachments = attachments;
    }

    return messageContext;
  }

  /**
   * Extracts file attachments from a CompletionMessage
   */
  private static extractFileAttachments(message: CompletionMessage): CompletionFileAttachment[] {
    const attachments: CompletionFileAttachment[] = [];

    // Handle single uploaded file (legacy support)
    if (message.uploaded_file) {
      attachments.push(this.fileUploadToAttachment(message.uploaded_file));
    }

    // Handle multiple file attachments
    if (message.file_attachments && message.file_attachments.length > 0) {
      message.file_attachments.forEach((file: FileUpload) => {
        attachments.push(this.fileUploadToAttachment(file));
      });
    }

    // Handle file_id reference (when only ID is available)
    const msg = message as any; // Cast to any for legacy properties
    if (msg.file_id && !message.uploaded_file && (!message.file_attachments || message.file_attachments.length === 0)) {
      // Create a minimal attachment with available info
      attachments.push({
        id: msg.file_id,
        name: msg.file_name || 'attachment',
        mimeType: msg.file_type || 'application/octet-stream',
        size: msg.file_size || 0,
        url: msg.file_url || '',
        extractedText: msg.file_extracted_text
      });
    }

    return attachments;
  }

  /**
   * Converts a FileUpload to CompletionFileAttachment
   */
  private static fileUploadToAttachment(file: FileUpload): CompletionFileAttachment {
    return {
      id: file.file_id,
      name: file.file_name,
      mimeType: file.file_mime,
      size: file.file_size,
      url: file.url || '',
      extractedText: file.content || undefined,
      content: file.content || undefined
    };
  }

  /**
   * Creates a CompletionRequest from chat parameters
   * Simplified since CompletionRequest now uses CompletionMessage directly
   */
  static createCompletionRequest(
    messages: Partial<CompletionMessage>[],
    model: string,
    workspaceId: string,
    options?: {
      temperature?: number;
      maxTokens?: number;
      stream?: boolean;
      topP?: number;
      frequencyPenalty?: number;
      presencePenalty?: number;
      stop?: string[];
      seed?: number;
      responseFormat?: { type: 'text' | 'json_object' };
    }
  ): CompletionRequest {
    return {
      model,
      messages: messages, // Direct assignment, no conversion needed
      workspaceId,
      stream: options?.stream ?? true,
      temperature: options?.temperature,
      maxTokens: options?.maxTokens,
      topP: options?.topP,
      frequencyPenalty: options?.frequencyPenalty,
      presencePenalty: options?.presencePenalty,
      stop: options?.stop,
      seed: options?.seed,
      responseFormat: options?.responseFormat
    };
  }

  /**
   * Converts legacy message format to MessageContext array
   * Used for backward compatibility with existing code
   */
  static fromLegacyFormat(messages: Array<{ role: string; content: string; file_id?: string }>): MessageContext[] {
    return messages.map(msg => ({
      role: msg.role as 'user' | 'assistant' | 'system',
      content: msg.content,
      // Note: Legacy format loses file metadata, only has ID
      attachments: msg.file_id ? [{
        id: msg.file_id,
        name: 'attachment',
        mimeType: 'application/octet-stream',
        size: 0,
        url: ''
      }] : undefined
    }));
  }

  /**
   * Converts MessageContext array to legacy format
   * Used for backward compatibility with existing endpoints
   */
  static toLegacyFormat(messages: MessageContext[]): Array<{ role: string; content: string; file_id?: string }> {
    return messages.map(msg => ({
      role: msg.role,
      content: msg.content,
      // Take first attachment ID for legacy single-file support
      file_id: msg.attachments?.[0]?.id
    }));
  }

  /**
   * Transforms a StreamingChunk to the format expected by the frontend
   */
  static transformStreamingChunk(chunk: StreamingChunk): any {
    // Map our unified chunk format to what the frontend expects
    if (chunk.type === 'content') {
      return {
        choices: [{
          delta: {
            content: chunk.content
          },
          finish_reason: null
        }]
      };
    } else if (chunk.type === 'done') {
      return {
        choices: [{
          delta: {},
          finish_reason: chunk.finishReason || 'stop'
        }],
        usage: chunk.usage ? {
          prompt_tokens: chunk.usage.promptTokens,
          completion_tokens: chunk.usage.completionTokens,
          total_tokens: chunk.usage.totalTokens
        } : undefined
      };
    } else if (chunk.type === 'error') {
      // Handle error chunks
      throw new Error(chunk.error?.message || 'Stream error occurred');
    }

    return null;
  }

  /**
   * Creates a CompletionMessage from a StreamingChunk response
   * Used when saving assistant responses
   */
  static createMessageFromResponse(
    chunk: StreamingChunk,
    conversationId: string,
    workspaceId: string,
    userId: string
  ): Partial<CompletionMessage> {
    return {
      content: chunk.content || '',
      role: 'assistant',
      conversation_id: conversationId,
      workspace_id: workspaceId,
      user_id: userId,
      created_at: new Date().toISOString(),
      prompt_tokens: chunk.usage?.promptTokens,
      completion_tokens: chunk.usage?.completionTokens,
      total_tokens: chunk.usage?.totalTokens
    };
  }

  /**
   * Validates and sanitizes messages before sending to API
   */
  static validateMessages(messages: MessageContext[]): MessageContext[] {
    return messages.filter(msg => {
      const validation = validateMessage(msg);
      if (!validation.isValid) {
        validation.errors.forEach(error => console.warn(error));
        return false;
      }
      return true;
    });
  }
}